/**
 * Assembly Form Fields Component
 * Contains all basic form fields for the Assembly Form Dialog
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Link,
  Paper,
  Alert,
  Checkbox,
  Popover
} from '@mui/material';
import { Add, OpenInNew, InfoOutlined } from '@mui/icons-material';
import axios from 'axios';
import { formatPartMasterOptionLabel } from '../../utils/partMasterLabel';

const AssemblyFormFields = ({
  formData,
  setFormData,
  dropdownOptions,
  filteredModelVariants,
  handleAssemblyTypeChange,
  handleManufacturerChange,
  handleNavigateToAddAssemblyType,
  handleNavigateToAddPart,
  handlePartMasterSelect,
  readOnly = false
}) => {
  // State for Part Master search
  const [partsOptions, setPartsOptions] = useState(dropdownOptions.parts || []);
  const [partsLoading, setPartsLoading] = useState(false);
  const [partsSearchInput, setPartsSearchInput] = useState('');
  const searchTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);
  const [installedOnDateInfoAnchor, setInstalledOnDateInfoAnchor] = useState(null);
  const [workOrderNumberInfoAnchor, setWorkOrderNumberInfoAnchor] = useState(null);

  // Initialize parts options from dropdownOptions
  useEffect(() => {
    if (dropdownOptions.parts && dropdownOptions.parts.length > 0) {
      setPartsOptions(dropdownOptions.parts);
    }
  }, [dropdownOptions.parts]);

  // Ensure selected part is in options
  useEffect(() => {
    if (formData.partId) {
      const partId = formData.partId?.toString() || formData.partId;
      const isPartInOptions = partsOptions.some(part => {
        const optionId = part._id?.toString() || part._id;
        return optionId === partId;
      });

      // If selected part is not in options, fetch it
      if (!isPartInOptions && !partsLoading) {
        axios.get(`/api/parts/${partId}`)
          .then(response => {
            if (response.data) {
              const p = response.data;
              setPartsOptions(prev => {
                const exists = prev.some(part => {
                  const pId = part._id?.toString() || part._id;
                  return pId === partId;
                });
                return exists ? prev : [...prev, p];
              });
              setFormData(prev => {
                if ((prev.partId?.toString() || prev.partId) !== partId) return prev;
                return {
                  ...prev,
                  partNumber: p.partNumber || '',
                  partName: (p.partName && String(p.partName).trim()) || ''
                };
              });
            }
          })
          .catch(error => {
            console.error('Error fetching selected part:', error);
          });
      }
    }
  }, [formData.partId, partsOptions, partsLoading, setFormData]);

  // Keep display fields in sync when the selected part appears in options (e.g. after dropdown refresh)
  useEffect(() => {
    if (!formData.partId) return;
    const id = formData.partId?.toString() || formData.partId;
    const part = partsOptions.find(p => (p._id?.toString() || p._id) === id);
    if (!part) return;
    const pn = part.partNumber || '';
    const name = (part.partName && String(part.partName).trim()) || '';
    setFormData(prev => {
      if ((prev.partId?.toString() || prev.partId) !== id) return prev;
      if (prev.partNumber === pn && prev.partName === name) return prev;
      return { ...prev, partNumber: pn, partName: name };
    });
  }, [formData.partId, partsOptions, setFormData]);

  // Fetch parts with search (optimized with request cancellation)
  const fetchParts = useCallback(async (searchTerm = '') => {
    // Cancel previous request if it exists
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    
    // Create new AbortController for this request
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    
    setPartsLoading(true);
    try {
      const params = {
        isActive: 'true',
        pageSize: 50, // Reduced from 100 - sufficient for dropdown
        page: 1
      };
      if (searchTerm && searchTerm.trim()) {
        params.search = searchTerm.trim();
      }
      const response = await axios.get('/api/parts', { 
        params,
        signal: abortController.signal // Cancel previous request if new one is made
      });
      
      // Only update if request wasn't aborted
      if (!abortController.signal.aborted) {
        const fetchedParts = response.data.parts || response.data || [];
        setPartsOptions(fetchedParts);
      }
    } catch (error) {
      // Ignore abort errors (expected when canceling previous requests)
      if (error.name !== 'CanceledError' && error.name !== 'AbortError' && error.code !== 'ERR_CANCELED') {
        console.error('Error fetching parts:', error);
        setPartsOptions([]);
      }
    } finally {
      // Only update loading state if this is still the current request
      if (!abortController.signal.aborted) {
        setPartsLoading(false);
      }
    }
  }, []);

  // Debounced search handler with minimum character threshold
  const handlePartsInputChange = useCallback((event, newInputValue, reason) => {
    setPartsSearchInput(newInputValue);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If user is typing (not selecting), debounce the search
    if (reason === 'input') {
      const trimmedValue = newInputValue.trim();
      
      // Only search if:
      // 1. User has typed at least 2 characters, OR
      // 2. Input is empty (show initial results)
      if (trimmedValue.length === 0 || trimmedValue.length >= 2) {
        searchTimeoutRef.current = setTimeout(() => {
          fetchParts(trimmedValue);
        }, 500); // Increased to 500ms debounce for better efficiency
      } else if (trimmedValue.length === 1) {
        // For single character, show initial list (no search)
        fetchParts('');
      }
    } else if (reason === 'clear') {
      // Reset to initial parts list when cleared
      fetchParts('');
    }
  }, [fetchParts]);

  // Cleanup timeout and abort controller on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  return (
    <>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
          <Autocomplete
            fullWidth
            options={dropdownOptions.assemblyTypes || []}
            getOptionLabel={(option) => {
              const name = option.name || '';
              const ataChapter = option.ataChapterId;
              if (ataChapter) {
                // Handle both populated (object) and unpopulated (ID) cases
                const ataCode = typeof ataChapter === 'object' && ataChapter !== null
                  ? ataChapter.code || ''
                  : '';
                return ataCode ? `${name} (${ataCode})` : name;
              }
              return name;
            }}
            value={dropdownOptions.assemblyTypes.find(at => {
              const atId = at._id ? String(at._id) : String(at);
              return atId === String(formData.type || '');
            }) || null}
            onChange={handleAssemblyTypeChange}
            renderOption={(props, option) => {
              const name = option.name || '';
              const ataChapter = option.ataChapterId;
              let ataCode = '';
              let ataName = '';
              
              if (ataChapter) {
                // Handle both populated (object) and unpopulated (ID) cases
                if (typeof ataChapter === 'object' && ataChapter !== null) {
                  ataCode = ataChapter.code || '';
                  ataName = ataChapter.name || '';
                }
              }
              
              return (
                <Box component="li" {...props} key={option._id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {name}
                    </Typography>
                    {ataCode && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.25 }}>
                        ATA Chapter: {ataCode} {ataName ? `- ${ataName}` : ''}
                      </Typography>
                    )}
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Assembly Type *"
                required
                sx={{
                  '& .MuiInputLabel-root': {
                    fontSize: '14px',
                    fontWeight: 600,
                    '&.Mui-focused': {
                      color: 'primary.main',
                    },
                  },
                  '& .MuiOutlinedInput-root': {
                    fontSize: '14px',
                    '& .MuiAutocomplete-input': {
                      overflow: 'visible',
                      textOverflow: 'clip',
                      whiteSpace: 'nowrap',
                    },
                  },
                }}
              />
            )}
            sx={{
              flex: 1,
              minWidth: 0, // Allow flex to shrink if needed
              width: '100%',
              '& .MuiAutocomplete-inputRoot': {
                '& .MuiOutlinedInput-notchedOutline': {
                  overflow: 'visible',
                },
                '& .MuiAutocomplete-input': {
                  overflow: 'visible !important',
                  textOverflow: 'clip !important',
                  whiteSpace: 'nowrap !important',
                  width: '100% !important',
                  minWidth: '0 !important',
                  flex: '1 1 auto !important',
                },
                '& .MuiAutocomplete-endAdornment': {
                  position: 'absolute',
                  right: '14px',
                },
              },
              '& .MuiAutocomplete-tag': {
                maxWidth: '100%',
                overflow: 'visible',
                textOverflow: 'clip',
              },
            }}
            PaperProps={{
              sx: {
                maxHeight: 400,
                minWidth: 600,
                width: 'auto',
                '& .MuiAutocomplete-listbox': {
                  maxHeight: 400,
                  minWidth: 600,
                  width: 'auto',
                },
              },
            }}
            ListboxProps={{
              sx: {
                maxHeight: 400,
                '& .MuiAutocomplete-option': {
                  color: '#000',
                  fontSize: '13px',
                  padding: '12px 16px',
                  minHeight: '48px',
                  whiteSpace: 'nowrap',
                  overflow: 'visible',
                  textOverflow: 'clip',
                  '&:hover': {
                    backgroundColor: '#f5f5f5',
                  },
                },
              },
            }}
            componentsProps={{
              popper: {
                style: {
                  minWidth: 600,
                },
              },
            }}
            noOptionsText="No assembly types available"
          />
          <IconButton
            onClick={handleNavigateToAddAssemblyType}
            color="primary"
            title="Add Assembly Type"
            sx={{ flexShrink: 0, mt: 0.5 }}
          >
            <Add />
          </IconButton>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required sx={{ minWidth: 200 }}>
          <InputLabel
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'text.primary',
              backgroundColor: 'background.paper',
              padding: '0 4px',
              whiteSpace: 'nowrap',
              overflow: 'visible',
              '&.MuiInputLabel-shrink': {
                fontSize: '14px',
                fontWeight: 600,
                transform: 'translate(14px, -9px) scale(0.75)',
                backgroundColor: 'background.paper',
                padding: '0 4px',
                color: 'text.primary',
              },
              '&.Mui-focused': {
                color: 'primary.main',
                fontWeight: 600,
              },
              '&.MuiInputLabel-root': {
                zIndex: 1,
              },
            }}
          >
            ATA Chapter
          </InputLabel>
          <Select
            value={formData.ataChapterId || ''}
            label="ATA Chapter"
            onChange={(e) => setFormData({ ...formData, ataChapterId: e.target.value })}
            disabled={!formData.type}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                overflow: 'visible',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    color: '#000',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }
                }
              }
            }}
          >
            {!formData.type ? (
              <MenuItem disabled sx={{ color: '#000' }}>
                Select assembly type first
              </MenuItem>
            ) : (
              dropdownOptions.ataChapters.map((ata) => (
                <MenuItem key={ata._id} value={ata._id} sx={{ color: '#000' }}>
                  {ata.code} - {ata.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required sx={{ minWidth: 200 }}>
          <InputLabel
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'text.primary',
              backgroundColor: 'background.paper',
              padding: '0 4px',
              whiteSpace: 'nowrap',
              overflow: 'visible',
              '&.MuiInputLabel-shrink': {
                fontSize: '14px',
                fontWeight: 600,
                transform: 'translate(14px, -9px) scale(0.75)',
                backgroundColor: 'background.paper',
                padding: '0 4px',
                color: 'text.primary',
              },
              '&.Mui-focused': {
                color: 'primary.main',
                fontWeight: 600,
              },
              '&.MuiInputLabel-root': {
                zIndex: 1,
              },
            }}
          >
            Manufacturer Name
          </InputLabel>
          <Tooltip 
            title={dropdownOptions.manufacturerNames?.find(mn => mn._id === formData.manufacturerNameId)?.name || ''}
            placement="top"
            arrow
            disableHoverListener={!formData.manufacturerNameId}
          >
            <Select
              value={formData.manufacturerNameId || ''}
              label="Manufacturer Name"
              onChange={handleManufacturerChange}
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  overflow: 'visible',
                },
              }}
              renderValue={(value) => {
                const manufacturerName = dropdownOptions.manufacturerNames?.find(mn => mn._id === value);
                return manufacturerName?.name || '';
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 400,
                    minWidth: 350,
                    '& .MuiMenuItem-root': {
                      whiteSpace: 'normal',
                      wordBreak: 'break-word',
                      padding: '10px 16px',
                      minHeight: 'auto',
                      lineHeight: 1.5,
                      overflow: 'visible',
                    },
                  },
                },
              }}
            >
              {(dropdownOptions.manufacturerNames || []).map((mn) => (
                <MenuItem key={mn._id} value={mn._id}>
                  {mn.name}
                </MenuItem>
              ))}
            </Select>
          </Tooltip>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required sx={{ minWidth: 200 }}>
          <InputLabel
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'text.primary',
              backgroundColor: 'background.paper',
              padding: '0 4px',
              whiteSpace: 'nowrap',
              overflow: 'visible',
              '&.MuiInputLabel-shrink': {
                fontSize: '14px',
                fontWeight: 600,
                transform: 'translate(14px, -9px) scale(0.75)',
                backgroundColor: 'background.paper',
                padding: '0 4px',
                color: 'text.primary',
              },
              '&.Mui-focused': {
                color: 'primary.main',
                fontWeight: 600,
              },
              '&.MuiInputLabel-root': {
                zIndex: 1,
              },
            }}
          >
            Model Variant
          </InputLabel>
          <Select
            value={formData.modelId || ''}
            label="Model Variant"
            onChange={(e) => setFormData({ ...formData, modelId: e.target.value })}
            disabled={!formData.manufacturerNameId}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                overflow: 'visible',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 400,
                  minWidth: 350,
                  '& .MuiMenuItem-root': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    padding: '10px 16px',
                    minHeight: 'auto',
                    lineHeight: 1.5,
                    overflow: 'visible',
                    color: '#000',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }
                }
              }
            }}
          >
            {filteredModelVariants.length === 0 ? (
              <MenuItem disabled sx={{ color: '#000' }}>
                {formData.manufacturerNameId
                  ? 'No model variants found for selected manufacturer'
                  : 'Select manufacturer name first'}
              </MenuItem>
            ) : (
              filteredModelVariants.map((model) => (
                <MenuItem key={model._id} value={model._id} sx={{ color: '#000' }}>
                  {model.name}
                </MenuItem>
              ))
            )}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Serial Number"
          value={formData.serialNumber}
          onChange={(e) => setFormData({ ...formData, serialNumber: e.target.value })}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Position"
          value={formData.position}
          onChange={(e) => setFormData({ ...formData, position: e.target.value })}
          required
          placeholder="e.g., LH, RH"
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <TextField
            fullWidth
            label="Installed On Date"
            type="date"
            value={formData.installedOnDate}
            onChange={(e) => setFormData({ ...formData, installedOnDate: e.target.value })}
            InputLabelProps={{ shrink: true }}
            required
          />
          <Tooltip title="Info">
            <IconButton
              size="small"
              color="info"
              onClick={(e) => setInstalledOnDateInfoAnchor(e.currentTarget)}
              sx={{ mt: 1 }}
              aria-label="Installed on date info"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Popover
            open={Boolean(installedOnDateInfoAnchor)}
            anchorEl={installedOnDateInfoAnchor}
            onClose={() => setInstalledOnDateInfoAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Alert severity="info" sx={{ m: 1.5, maxWidth: 360 }}>
              For initial load (e.g. aircraft purchase), use as the reference date for the entered times (e.g. delivery or purchase date).
            </Alert>
          </Popover>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth required sx={{ minWidth: 200 }}>
          <InputLabel
            sx={{
              fontSize: '14px',
              fontWeight: 600,
              color: 'text.primary',
              backgroundColor: 'background.paper',
              padding: '0 4px',
              whiteSpace: 'nowrap',
              overflow: 'visible',
              '&.MuiInputLabel-shrink': {
                fontSize: '14px',
                fontWeight: 600,
                transform: 'translate(14px, -9px) scale(0.75)',
                backgroundColor: 'background.paper',
                padding: '0 4px',
                color: 'text.primary',
              },
              '&.Mui-focused': {
                color: 'primary.main',
                fontWeight: 600,
              },
              '&.MuiInputLabel-root': {
                zIndex: 1,
              },
            }}
          >
            License
          </InputLabel>
          <Select
            value={formData.licenseId}
            label="License"
            onChange={(e) => setFormData({ ...formData, licenseId: e.target.value })}
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                overflow: 'visible',
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  '& .MuiMenuItem-root': {
                    color: '#000',
                    '&:hover': {
                      backgroundColor: '#f5f5f5'
                    }
                  }
                }
              }
            }}
          >
            {dropdownOptions.licenses.map((license) => (
              <MenuItem key={license._id} value={license._id} sx={{ color: '#000' }}>
                {license.licenseNumber} - {license.employeeName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!formData.isInitialLoad}
              onChange={(e) => setFormData({
                ...formData,
                isInitialLoad: e.target.checked,
                workOrderNumber: e.target.checked ? 'NA - Initial Installation' : (formData.workOrderNumber === 'NA - Initial Installation' ? '' : formData.workOrderNumber || '')
              })}
            />
          }
          label="Initial load (e.g. aircraft purchase)"
        />
        <Typography variant="caption" display="block" sx={{ mt: 0.5, color: 'text.secondary' }}>
          Check when adding an assembly that was already on the aircraft (no work order for this installation).
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5 }}>
          <TextField
            fullWidth
            label="Work Order Number"
            value={formData.workOrderNumber || ''}
            onChange={(e) => setFormData({ ...formData, workOrderNumber: e.target.value })}
          />
          <Tooltip title="Info">
            <IconButton
              size="small"
              color="info"
              onClick={(e) => setWorkOrderNumberInfoAnchor(e.currentTarget)}
              sx={{ mt: 1 }}
              aria-label="Work order number info"
            >
              <InfoOutlined fontSize="small" />
            </IconButton>
          </Tooltip>
          <Popover
            open={Boolean(workOrderNumberInfoAnchor)}
            anchorEl={workOrderNumberInfoAnchor}
            onClose={() => setWorkOrderNumberInfoAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <Alert severity="info" sx={{ m: 1.5, maxWidth: 360 }}>
              For initial load (e.g. aircraft purchase), leave Work Order blank and enter current utilization in Time Since Records.
            </Alert>
          </Popover>
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth sx={{ minWidth: 200 }}>
          <InputLabel>As-received evidence from</InputLabel>
          <Select
            value={formData.asReceivedEvidenceFrom || ''}
            label="As-received evidence from"
            onChange={(e) => setFormData({ ...formData, asReceivedEvidenceFrom: e.target.value || undefined })}
            displayEmpty
          >
            <MenuItem value="">—</MenuItem>
            <MenuItem value="logbook">Logbook</MenuItem>
            <MenuItem value="seller">Seller</MenuItem>
            <MenuItem value="OEM">OEM</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Autocomplete
            fullWidth
            options={partsOptions}
            loading={partsLoading}
            disabled={readOnly}
            getOptionLabel={(option) => formatPartMasterOptionLabel(option)}
            isOptionEqualToValue={(option, value) => {
              const optionId = option._id?.toString() || option._id;
              const valueId = value._id?.toString() || value._id;
              return optionId === valueId;
            }}
            value={partsOptions.find(part => {
              const partId = part._id?.toString() || part._id;
              return partId === (formData.partId?.toString() || formData.partId);
            }) || null}
            onChange={(event, newValue) => {
              if (handlePartMasterSelect) {
                handlePartMasterSelect(newValue || null);
              } else {
                const pid = newValue ? (newValue._id?.toString() || newValue._id) : '';
                setFormData({
                  ...formData,
                  partId: pid,
                  partNumber: newValue ? (newValue.partNumber || '') : '',
                  partName: newValue
                    ? ((newValue.partName && String(newValue.partName).trim()) || '')
                    : ''
                });
              }
            }}
            onInputChange={handlePartsInputChange}
            inputValue={partsSearchInput}
            filterOptions={(options) => options} // Disable client-side filtering since we're doing server-side search
            renderInput={(params) => (
              <TextField
                {...params}
                label="Part Master"
                sx={{ minWidth: 200 }}
              />
            )}
            sx={{ minWidth: 200 }}
            ListboxProps={{
              style: {
                maxHeight: 400
              }
            }}
            PaperComponent={({ children, ...other }) => (
              <Paper
                {...other}
                sx={{
                  maxHeight: 400,
                  minWidth: 700,
                  width: 'auto',
                  backgroundColor: '#ffffff',
                  '& .MuiAutocomplete-option': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    padding: '12px 16px',
                    minHeight: 'auto',
                    lineHeight: 1.5,
                    overflow: 'visible',
                    textOverflow: 'unset',
                    color: '#212121',
                    fontSize: '14px',
                    fontWeight: 400,
                    backgroundColor: '#ffffff',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      color: '#212121'
                    },
                    '&.Mui-focused': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2'
                    },
                    '&[aria-selected="true"]': {
                      backgroundColor: '#e3f2fd',
                      color: '#1976d2',
                      '&:hover': {
                        backgroundColor: '#bbdefb'
                      }
                    }
                  }
                }}
              >
                {children}
              </Paper>
            )}
          />
          {!readOnly && (
            <Link
              component="button"
              type="button"
              onClick={handleNavigateToAddPart}
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                fontSize: '0.875rem',
                textDecoration: 'none',
                whiteSpace: 'nowrap',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              Add Part
              <OpenInNew sx={{ fontSize: '0.875rem' }} />
            </Link>
          )}
        </Box>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Part Number"
          value={formData.partNumber || ''}
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Part Name"
          value={formData.partName || ''}
          disabled
          InputProps={{ readOnly: true }}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Notes"
          value={formData.notes || ''}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
          multiline
          rows={5}
          minRows={4}
          sx={{ minWidth: 320, '& .MuiInputBase-root': { minHeight: 120 } }}
        />
      </Grid>
    </>
  );
};

export default AssemblyFormFields;
